import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Box, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Alert } from '@mui/material';
import API from '@utils/axios';
import { formatDateTime } from '@utils/helpers';
import { useDialog } from '@contexts/DialogContext';
import CopyableTextField from '@components/CopyableTextField';

function UserApiKey({ user, generateUrl, onGenerated }) {
    const { t } = useTranslation();
    const { requestConfirm } = useDialog();
    const [apiKey, setApiKey] = useState(null);
    const [showApiKey, setShowApiKey] = useState(false);

    const handleGenerate = async () => {
        try {
            const response = await API.post(generateUrl);
            if (response.status === 200 && response.data.api_key) {
                setApiKey(response.data.api_key);
                setShowApiKey(true);
                if (typeof onGenerated === 'function') {
                    onGenerated();
                }
            }
        } catch (error) {
            console.error('Error:', error);
        }
    };

    const handleGenerateConfirm = () => {
        requestConfirm({
            message: t('Are you sure you want to generate a new API key? The current API key will be invalidated.'),
            onConfirm: handleGenerate,
        });
    };

    return (
        <>
            <Box>
                <Typography variant="h6">{t('API key')}</Typography>
                {user.api_key ? (
                    <>
                        <Typography variant="body1">{t('An API key has been generated for this user.')}</Typography>
                        <Typography variant="body1">{t('Generated date: {{date}}', { date: formatDateTime(user.api_key_created_at) })}</Typography>
                        <Typography variant="body1">{t('Last use date: {{date}}', { date: user.api_key_used_at ? formatDateTime(user.api_key_used_at) : t('Never') })}</Typography>
                        <Button variant="contained" color="default" onClick={handleGenerateConfirm} sx={{ mt: 1 }}>
                            {t('Regenerate API key')}
                        </Button>
                    </>
                ) : (
                    <>
                        <Typography variant="body1">{t('No API key has been generated for this user.')}</Typography>
                        <Button variant="contained" color="default" onClick={handleGenerate} sx={{ mt: 1 }}>
                            {t('Generate API key')}
                        </Button>
                    </>
                )}
            </Box>

            <Dialog
                open={showApiKey}
                onClose={() => setShowApiKey(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>{t('API key')}</DialogTitle>
                <DialogContent>
                    <Alert severity="error">
                        {t("Please carefully copy and save this API key now. You will not be able to retrieve it later.")}
                    </Alert>
                    <Box mt={2}>
                        <CopyableTextField value={apiKey ?? ''} minRows={3} />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowApiKey(false)} color="primary" variant="contained">
                        {t('Close')}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

export default UserApiKey;